import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const STEPS = [
    { id: 1, title: "Dados do cliente", subtitle: "Identificação, esfera, plano e datas" },
    { id: 2, title: "Ponto focal e fiscal", subtitle: "Contatos do cliente" },
    { id: 3, title: "Contexto inicial", subtitle: "Áreas, módulo prioritário e expectativas" },
];

const ESFERAS = [
    { value: "federal", label: "Federal" },
    { value: "estadual", label: "Estadual" },
    { value: "municipal", label: "Municipal" },
];

export default function NewContract() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        organizacao_nome: "",
        esfera: "municipal",
        plano: "premium",
        data_assinatura: new Date().toISOString().slice(0, 10),
        data_vencimento: new Date(new Date().getTime() + 360 * 86400000).toISOString().slice(0, 10),
        data_entrega_produto: "",
        vendedor_responsavel: "",
        ponto_focal: {
            nome: "", cargo: "", email: "", telefone: "",
            fiscal_nome: "", fiscal_email: "", fiscal_telefone: "",
        },
        contexto_inicial: { areas: "", num_usuarios: 0, modulo_prioritario: "Documentos Gerados", dificuldades: "", expectativas: "" },
    });

    function setField(path, value) {
        setForm(prev => {
            const next = { ...prev };
            const parts = path.split(".");
            let ref = next;
            for (let i = 0; i < parts.length - 1; i++) {
                ref[parts[i]] = { ...ref[parts[i]] };
                ref = ref[parts[i]];
            }
            ref[parts[parts.length - 1]] = value;
            return next;
        });
    }

    async function submit() {
        setError(""); setSaving(true);
        try {
            const payload = {
                ...form,
                data_entrega_produto: form.data_entrega_produto || null,
                contexto_inicial: { ...form.contexto_inicial, num_usuarios: Number(form.contexto_inicial.num_usuarios) || 0 },
            };
            const { data } = await api.post("/contracts", payload);
            navigate(`/contratos/${data.id}`);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Erro ao criar contrato");
        } finally { setSaving(false); }
    }

    return (
        <div className="space-y-6 fade-in max-w-3xl" data-testid="new-contract-page">
            <header>
                <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-[#0F2044] inline-flex items-center gap-1" data-testid="back-button">
                    <ArrowLeft className="h-3 w-3" /> Voltar
                </button>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium mt-3">Novo contrato</div>
                <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Cadastro em 3 etapas</h1>
                <p className="text-sm text-slate-500 mt-1">Ao salvar, geramos automaticamente o checklist completo da jornada com prazos calculados a partir da data de assinatura.</p>
            </header>

            <ol className="grid grid-cols-3 gap-3" data-testid="stepper">
                {STEPS.map((s) => (
                    <li key={s.id} className={`border rounded-md px-4 py-3 ${step === s.id ? "border-[#0F2044] bg-white" : step > s.id ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                        <div className="flex items-center gap-2">
                            <div className={`h-6 w-6 rounded-full text-xs font-semibold flex items-center justify-center ${step > s.id ? "bg-emerald-500 text-white" : step === s.id ? "bg-[#0F2044] text-white" : "bg-slate-200 text-slate-500"}`}>
                                {step > s.id ? <Check className="h-3.5 w-3.5" /> : s.id}
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-[#0F2044]">{s.title}</div>
                                <div className="text-[11px] text-slate-500">{s.subtitle}</div>
                            </div>
                        </div>
                    </li>
                ))}
            </ol>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
                {step === 1 && (
                    <>
                        <Two>
                            <Field label="Nome da organização *">
                                <input required value={form.organizacao_nome} onChange={(e) => setField("organizacao_nome", e.target.value)} data-testid="form-org-input" className="input" />
                            </Field>
                            <Field label="Esfera *">
                                <select required value={form.esfera} onChange={(e) => setField("esfera", e.target.value)} data-testid="form-esfera-input" className="input">
                                    {ESFERAS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </Field>
                        </Two>
                        <Two>
                            <Field label="Plano *">
                                <select required value={form.plano} onChange={(e) => setField("plano", e.target.value)} data-testid="form-plano-input" className="input">
                                    <option value="premium">Premium — 600 créditos/mês</option>
                                    <option value="personalizado">Personalizado — 2.500 créditos/mês</option>
                                    <option value="especial">Especial — 6.000 consultas + 1.500 documentos</option>
                                </select>
                            </Field>
                            <Field label="Vendedor responsável *">
                                <input required value={form.vendedor_responsavel} onChange={(e) => setField("vendedor_responsavel", e.target.value)} data-testid="form-vendedor-input" className="input" />
                            </Field>
                        </Two>
                        <Two>
                            <Field label="Data de assinatura *">
                                <input type="date" required value={form.data_assinatura} onChange={(e) => setField("data_assinatura", e.target.value)} data-testid="form-sig-input" className="input" />
                            </Field>
                            <Field label="Data de vencimento *">
                                <input type="date" required value={form.data_vencimento} onChange={(e) => setField("data_vencimento", e.target.value)} data-testid="form-venc-input" className="input" />
                            </Field>
                        </Two>
                        <Field label="Data de entrega do produto">
                            <input type="date" value={form.data_entrega_produto || ""} onChange={(e) => setField("data_entrega_produto", e.target.value)} data-testid="form-entrega-input" className="input" />
                        </Field>
                    </>
                )}
                {step === 2 && (
                    <>
                        <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Ponto Focal</div>
                        <Two>
                            <Field label="Nome do ponto focal">
                                <input value={form.ponto_focal.nome} onChange={(e) => setField("ponto_focal.nome", e.target.value)} data-testid="form-pf-nome" className="input" />
                            </Field>
                            <Field label="Cargo">
                                <input value={form.ponto_focal.cargo} onChange={(e) => setField("ponto_focal.cargo", e.target.value)} data-testid="form-pf-cargo" className="input" />
                            </Field>
                        </Two>
                        <Two>
                            <Field label="E-mail">
                                <input type="email" value={form.ponto_focal.email} onChange={(e) => setField("ponto_focal.email", e.target.value)} data-testid="form-pf-email" className="input" />
                            </Field>
                            <Field label="Telefone">
                                <input value={form.ponto_focal.telefone} onChange={(e) => setField("ponto_focal.telefone", e.target.value)} data-testid="form-pf-tel" className="input" />
                            </Field>
                        </Two>

                        <div className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold pt-3 border-t border-slate-200">Fiscal do contrato</div>
                        <Field label="Nome do fiscal">
                            <input value={form.ponto_focal.fiscal_nome} onChange={(e) => setField("ponto_focal.fiscal_nome", e.target.value)} data-testid="form-fiscal-nome" className="input" />
                        </Field>
                        <Two>
                            <Field label="E-mail do fiscal">
                                <input type="email" value={form.ponto_focal.fiscal_email} onChange={(e) => setField("ponto_focal.fiscal_email", e.target.value)} data-testid="form-fiscal-email" className="input" />
                            </Field>
                            <Field label="Telefone do fiscal">
                                <input value={form.ponto_focal.fiscal_telefone} onChange={(e) => setField("ponto_focal.fiscal_telefone", e.target.value)} data-testid="form-fiscal-tel" className="input" />
                            </Field>
                        </Two>
                    </>
                )}
                {step === 3 && (
                    <>
                        <Field label="Áreas que utilizarão o sistema">
                            <input value={form.contexto_inicial.areas} onChange={(e) => setField("contexto_inicial.areas", e.target.value)} data-testid="form-areas-input" className="input" />
                        </Field>
                        <Two>
                            <Field label="Número de usuários">
                                <input type="number" min={0} value={form.contexto_inicial.num_usuarios} onChange={(e) => setField("contexto_inicial.num_usuarios", e.target.value)} data-testid="form-users-input" className="input" />
                            </Field>
                            <Field label="Módulo prioritário">
                                <select value={form.contexto_inicial.modulo_prioritario} onChange={(e) => setField("contexto_inicial.modulo_prioritario", e.target.value)} data-testid="form-mod-input" className="input">
                                    <option>Mensagens/Consultas</option>
                                    <option>Documentos Gerados</option>
                                    <option>Analisador</option>
                                </select>
                            </Field>
                        </Two>
                        <Field label="Principais dificuldades atuais">
                            <textarea rows={2} value={form.contexto_inicial.dificuldades} onChange={(e) => setField("contexto_inicial.dificuldades", e.target.value)} data-testid="form-dif-input" className="input" />
                        </Field>
                        <Field label="Expectativas">
                            <textarea rows={2} value={form.contexto_inicial.expectativas} onChange={(e) => setField("contexto_inicial.expectativas", e.target.value)} data-testid="form-exp-input" className="input" />
                        </Field>
                    </>
                )}

                {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

                <div className="flex justify-between pt-2">
                    <button
                        type="button"
                        onClick={() => setStep(Math.max(1, step - 1))}
                        disabled={step === 1}
                        data-testid="step-back-button"
                        className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Voltar
                    </button>
                    {step < 3 ? (
                        <button
                            type="button"
                            onClick={() => setStep(step + 1)}
                            data-testid="step-next-button"
                            className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md inline-flex items-center gap-1"
                        >
                            Avançar <ArrowRight className="h-3.5 w-3.5" />
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={submit}
                            disabled={saving}
                            data-testid="form-submit-button"
                            className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white font-semibold rounded-md inline-flex items-center gap-1 disabled:opacity-60"
                        >
                            {saving ? "Salvando…" : "Salvar contrato"} <Check className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Two({ children }) { return <div className="grid grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            {children}
        </div>
    );
}
